/**
 * AdminOperatingModel Page
 * Business Operating Model infographic layout for organization plan configuration.
 * Admin-only — requires Cognito 'admins' group membership.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Shield, ShieldAlert, Landmark } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@/contexts/AuthContext';
import { PageLayout } from '@/components';
import { UnauthenticatedPage } from '@/components/ui';

const BOM_COMPONENTS = [
  {
    number: 1,
    titleKey: 'admin.operatingModel.guidingPrinciplesTitle',
    descriptionKey: 'admin.operatingModel.guidingPrinciplesDesc',
  },
  {
    number: 2,
    titleKey: 'admin.operatingModel.orgStructureTitle',
    descriptionKey: 'admin.operatingModel.orgStructureDesc',
  },
  {
    number: 3,
    titleKey: 'admin.operatingModel.processesTitle',
    descriptionKey: 'admin.operatingModel.processesDesc',
  },
  {
    number: 4,
    titleKey: 'admin.operatingModel.technologyTitle',
    descriptionKey: 'admin.operatingModel.technologyDesc',
  },
  {
    number: 5,
    titleKey: 'admin.operatingModel.peopleTitle',
    descriptionKey: 'admin.operatingModel.peopleDesc',
  },
  {
    number: 6,
    titleKey: 'admin.operatingModel.cultureTitle',
    descriptionKey: 'admin.operatingModel.cultureDesc',
  },
];

const AdminOperatingModel: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading: authLoading, isAdmin } = useAuthContext();

  if (authLoading) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-12 sm:pt-24">
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!isAuthenticated) {
    return (
      <UnauthenticatedPage
        icon={<Shield className="w-8 h-8 text-slate-400" />}
        title={t('admin.operatingModel.unauthTitle')}
        description={t('admin.operatingModel.unauthDescription')}
      />
    );
  }

  if (!isAdmin) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-12 sm:pt-24">
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="p-3 bg-red-100 rounded-full mb-4">
              <ShieldAlert className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">{t('admin.operatingModel.accessDenied')}</h2>
            <p className="text-slate-500">{t('admin.operatingModel.noPermission')}</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto px-6 pt-20 pb-12 sm:pt-24">
        {/* Header */}
        <div className="mb-10">
          <Link
            to="/admin"
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('admin.operatingModel.backToAdmin')}
          </Link>

          <div className="flex items-center gap-4">
            <div className="p-2.5 bg-emerald-100 rounded-xl">
              <Landmark className="w-7 h-7 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{t('admin.operatingModel.title')}</h1>
              <p className="text-slate-500 mt-0.5">
                {t('admin.operatingModel.subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {BOM_COMPONENTS.map((component) => (
            <button
              key={component.number}
              onClick={() => console.log(`Clicked ${t(component.titleKey)}`)}
              className="group relative flex flex-col items-start text-left bg-white border-2 border-emerald-200 rounded-xl p-6 shadow-sm cursor-pointer transition-all duration-200 hover:scale-105 hover:border-emerald-500 hover:shadow-lg hover:shadow-emerald-100/50 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
            >
              {/* Number Circle */}
              <div className="flex items-center justify-center w-11 h-11 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 text-white text-lg font-bold shadow-md shadow-emerald-200/50 mb-4">
                {component.number}
              </div>

              {/* Title */}
              <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-emerald-700 transition-colors">
                {t(component.titleKey)}
              </h3>

              {/* Description */}
              <p className="text-sm text-slate-500 leading-relaxed">
                {t(component.descriptionKey)}
              </p>
            </button>
          ))}
        </div>
      </div>
    </PageLayout>
  );
};

export default AdminOperatingModel;
